import TwilioVoiceClient from 'dashboard/api/channel/voice/twilioVoiceClient';
import RuutVoiceClient from 'dashboard/api/channel/voice/ruutVoiceClient';
import { cleanupWhatsappSession } from 'dashboard/composables/useWhatsappCallSession';
import { VOICE_CALL_PROVIDERS } from 'dashboard/helper/inbox';
import { TERMINAL_STATUSES } from 'dashboard/helper/voice';
import { defineStore } from 'pinia';

const teardownByProvider = call => {
  if (!call) return;

  if (call?.provider === VOICE_CALL_PROVIDERS.WHATSAPP) {
    cleanupWhatsappSession();
  } else if (call?.provider === VOICE_CALL_PROVIDERS.RUUT_VOICE) {
    RuutVoiceClient.endClientCall();
  } else {
    TwilioVoiceClient.endClientCall();
  }
};

export const useCallsStore = defineStore('calls', {
  state: () => ({
    calls: [],
  }),

  getters: {
    activeCall: state => state.calls.find(call => call.isActive) || null,
    hasActiveCall: state => state.calls.some(call => call.isActive),
    incomingCalls: state => state.calls.filter(call => !call.isActive),
    hasIncomingCall: state => state.calls.some(call => !call.isActive),
  },

  actions: {
    handleCallStatusChanged({ callSid, status }) {
      const call = this.calls.find(c => c.callSid === callSid);

      if (!TERMINAL_STATUSES.includes(status)) {
        // Track live status (ringing → in-progress) so the UI can start the
        // duration timer at answer time instead of at agent join.
        if (call) {
          call.status = status;
          // Outbound calls have no local "join" action. The provider pickup
          // webhook is the authoritative transition that promotes their
          // ringing card into the active-call UI.
          if (status === 'in-progress' && call.callDirection === 'outbound') {
            this.setCallActive(callSid);
          }
        }
        return;
      }

      // WhatsApp recordings live in the in-memory recorder until voice_call.ended
      // uploads them; tearing down here would race-wipe those chunks.
      if (call?.provider === 'whatsapp') {
        this.calls = this.calls.filter(c => c.callSid !== callSid);
        return;
      }

      this.removeCall(callSid);
    },

    addCall(callData) {
      if (!callData?.callSid) return;
      const existing = this.calls.find(c => c.callSid === callData.callSid);
      if (existing) {
        // Merge so a later cable event with sdp_offer/provider/caller fills in
        // gaps left by the earlier message.created path (and vice versa).
        // Preserve a previously-captured caller snapshot when the incoming
        // event has no sender info, otherwise the widget would flip to
        // "Unknown caller" on the next status update.
        const next = { ...callData };
        if (existing.caller && !next.caller) delete next.caller;
        if (!Object.hasOwn(next, 'isActive')) next.isActive = existing.isActive;
        Object.assign(existing, next);
        return;
      }

      // Prepend so the newest call surfaces as the primary card (incomingCalls[0])
      // and older calls drop into the stack below it.
      this.calls.unshift({
        ...callData,
        isActive: Boolean(callData.isActive),
      });
    },

    removeCall(callSid) {
      const callToRemove = this.calls.find(c => c.callSid === callSid);
      if (callToRemove?.isActive) {
        teardownByProvider(callToRemove);
      }
      this.calls = this.calls.filter(c => c.callSid !== callSid);
    },

    setCallActive(callSid) {
      this.calls = this.calls.map(call => ({
        ...call,
        isActive: call.callSid === callSid,
        connectionState:
          call.callSid === callSid ? 'connected' : call.connectionState,
      }));
    },

    setActiveConnectionState(connectionState) {
      const active = this.calls.find(call => call.isActive);
      if (active) active.connectionState = connectionState;
    },

    clearActiveCall() {
      const active = this.calls.find(c => c.isActive);
      teardownByProvider(active);
      this.calls = this.calls.filter(call => !call.isActive);
    },

    dismissCall(callSid) {
      this.calls = this.calls.filter(call => call.callSid !== callSid);
    },

    reconcileServerCalls(serverCalls) {
      const serverCallSids = new Set(serverCalls.map(call => call.callSid));
      const localOnlyCalls = this.calls.filter(
        call =>
          call.provider === VOICE_CALL_PROVIDERS.WHATSAPP ||
          serverCallSids.has(call.callSid)
      );

      this.calls = localOnlyCalls;
      serverCalls.forEach(call => this.addCall(call));
    },

    removeCallsForConversation(conversationId) {
      const callsToRemove = this.calls.filter(
        call => call.conversationId === conversationId
      );

      // Tear down each active call via its own provider so a WhatsApp call
      // gets cleanupWhatsappSession() (closes pc, stops recorder/mic) instead
      // of the Twilio-only endClientCall() — otherwise mic stays open.
      callsToRemove.filter(call => call.isActive).forEach(teardownByProvider);

      this.calls = this.calls.filter(
        call => call.conversationId !== conversationId
      );
    },
  },
});
